import * as React from 'react';
import { Download, FileText, Pause, Play } from 'lucide-react';
import type { ChatMessage } from '@bits/shared/zod';
import { cn } from '@/lib/utils';
import { formatBytes, formatDuration, isImageAttachment } from '@/lib/chat-model';

// Three shapes for one `attachment`: an image shows itself, a voice note gets
// a player, anything else is a card that downloads. The URL is the
// membership-checked route, so a plain <img> or <audio> can use it directly.
export function AttachmentView({
  message,
  mine,
}: {
  message: ChatMessage;
  mine: boolean;
}): React.ReactElement | null {
  const attachment = message.attachment;
  if (!attachment) return null;
  if (message.kind === 'voice') {
    return (
      <VoicePlayer
        src={attachment.url}
        durationMs={attachment.durationMs ?? 0}
        fileName={attachment.fileName}
        mine={mine}
      />
    );
  }
  if (isImageAttachment(message)) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn('-mx-1.5 -mt-0.5 block overflow-hidden rounded-xl', message.body ? 'mb-1.5' : '')}
        aria-label={`Open ${attachment.fileName}`}
      >
        <img
          src={attachment.url}
          alt={attachment.fileName}
          loading="lazy"
          className="max-h-80 w-auto max-w-full object-cover"
        />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      download={attachment.fileName}
      className={cn(
        'flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors',
        mine
          ? 'bg-[hsl(var(--brand-foreground))]/10 hover:bg-[hsl(var(--brand-foreground))]/20'
          : 'bg-[hsl(var(--muted))] hover:bg-[hsl(var(--accent))]',
        message.body ? 'mb-1.5' : '',
      )}
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          mine ? 'bg-[hsl(var(--brand-foreground))]/15' : 'bg-[hsl(var(--card))]',
        )}
      >
        <FileText className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{attachment.fileName}</span>
        <span className={cn('block text-[11px]', mine ? 'opacity-80' : 'text-[hsl(var(--muted-foreground))]')}>
          {formatBytes(attachment.sizeBytes)}
        </span>
      </span>
      <Download className="size-4 shrink-0 opacity-70" />
    </a>
  );
}

const SPEEDS = [1, 1.5, 2] as const;

export function VoicePlayer({
  src,
  durationMs,
  fileName,
  mine,
}: {
  src: string;
  durationMs: number;
  fileName: string;
  mine: boolean;
}): React.ReactElement {
  const audio = React.useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [position, setPosition] = React.useState(0);
  const [speed, setSpeed] = React.useState<(typeof SPEEDS)[number]>(1);
  const [failed, setFailed] = React.useState(false);
  // The stored duration wins: a WebM from Chromium reports Infinity until it
  // has been played through once.
  const durationS = durationMs / 1000;
  const fraction = durationS > 0 ? Math.min(1, position / durationS) : 0;

  const toggle = (): void => {
    const el = audio.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    el.playbackRate = speed;
    el.play().catch(() => setFailed(true));
  };

  const seek = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const el = audio.current;
    if (!el || durationS <= 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const next = ((event.clientX - rect.left) / rect.width) * durationS;
    el.currentTime = Math.max(0, Math.min(durationS, next));
    setPosition(el.currentTime);
  };

  const cycleSpeed = (): void => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]!;
    setSpeed(next);
    if (audio.current) audio.current.playbackRate = next;
  };

  const tone = mine ? 'text-[hsl(var(--brand-foreground))]' : 'text-[hsl(var(--foreground))]';
  const track = mine ? 'bg-[hsl(var(--brand-foreground))]/25' : 'bg-[hsl(var(--border))]';
  const fill = mine ? 'bg-[hsl(var(--brand-foreground))]' : 'bg-[hsl(var(--brand))]';

  return (
    <div className={cn('flex w-60 max-w-full items-center gap-2.5 py-0.5', tone)} aria-label="Voice note">
      <audio
        ref={audio}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
        onError={() => setFailed(true)}
      />
      <button
        type="button"
        onClick={toggle}
        disabled={failed}
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-full disabled:opacity-40',
          mine
            ? 'bg-[hsl(var(--brand-foreground))] text-[hsl(var(--brand))]'
            : 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]',
        )}
      >
        {playing ? <Pause className="size-4" /> : <Play className="size-4 translate-x-px" />}
      </button>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <button
          type="button"
          onClick={seek}
          aria-label="Seek"
          className={cn('relative h-1.5 w-full overflow-hidden rounded-full', track)}
        >
          <span className={cn('absolute inset-y-0 left-0 rounded-full', fill)} style={{ width: `${fraction * 100}%` }} />
        </button>
        <span className="flex items-center justify-between text-[11px] tabular-nums opacity-80">
          <span>
            {formatDuration(playing || position > 0 ? position : durationS)}
            {failed ? ' · cannot play here' : ''}
          </span>
          <span className="flex items-center gap-2">
            <button type="button" onClick={cycleSpeed} aria-label="Playback speed" className="font-medium hover:underline">
              {speed}×
            </button>
            <a href={src} download={fileName} aria-label="Download voice note" className="hover:underline">
              <Download className="size-3.5" />
            </a>
          </span>
        </span>
      </span>
    </div>
  );
}
